import { useEffect, useState, useRef, useCallback } from 'react'
import CssBaseline from '@mui/material/CssBaseline'
import { ThemeProvider } from '@mui/material/styles'
import type { Root } from 'react-dom/client'
import { modalConfig } from 'reusable/mui/modals/modal-theme'
import { Timeouts } from '@gap/utils/date-utils'
import type { SnackbarOptions } from './snackbar'
import GapSnackbar from './GapSnackbar'

/**
 * Imperative lifecycle wrapper for the snackbar helpers in `./snackbar`.
 * Mounted outside the React tree via `reactRender`, so it provides its own
 * `ThemeProvider` and tears itself down on close. Rendering lives in `GapSnackbar`.
 */
export const SnackbarComponent = ({
   title,
   autoHideDuration,
   variant = 'info',
   container,
   root,
   actions = [],
}: SnackbarOptions & { container: HTMLElement, root: Root }) => {
   const removed = useRef(false)
   const [open, setOpen] = useState(true)

   useEffect(() => {
      return () => {
         if (removed.current) return

         document.body.removeChild(container)
         removed.current = true
      }
   })

   const handleClose = useCallback(() => {
      setOpen(false)
      setTimeout(() => root.unmount())
   }, [root])

   return (
      <ThemeProvider theme={modalConfig.theme}>
         <CssBaseline />
         <GapSnackbar
            open={open}
            variant={variant}
            message={title}
            actions={actions}
            autoHideDuration={autoHideDuration || Timeouts.second * 4}
            onClose={handleClose}
         />
      </ThemeProvider>
   )
}
